import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import axios from "axios";
import { Country, State, City } from "country-state-city";

import Header from "../Include/Header";
import Sidebar from "../Include/Sidebar";
import PoDetails from "./components/PoDetails";
import PoItems from "./components/PoItems";
import PoTermsAndTax from "./components/PoTermsAndTax";

import { addAdminPurchaseOrder } from "../../../Store/Admin/believe/purchaseOrder/AddPo";
import { updateAdminPurchaseOrder } from "../../../Store/Admin/believe/purchaseOrder/UpdatePo";
import {
  getAdminOnePurchaseOrder,
  resetGetAdminOnePo,
} from "../../../Store/Admin/believe/purchaseOrder/GetOnePo";
import { getFirm } from "../../../Store/Admin/Firm/Firm";
import { DEFAULT_PO_NOTES_HTML, DEFAULT_COMMON_MAKE } from "../../../helper/purchaseOrder";
import { V_URL } from "../../../BaseUrl";

const initialItemState = {
  item_description: "",
  weight: "",
  unit: "",
  pcs: "",
  rate: "",
  amount: 0,
};

const initialState = {
  firm_id: "",
  po_no: "",
  po_date: moment().format("YYYY-MM-DD"),
  vendor_id: "",
  vendor_snapshot: {
    name: "",
    address_line1: "",
    address_line2: "",
    city: "Surat",
    state: "Gujarat",
    pincode: "",
    gst_no: "",
  },
  order_details: {
    contact_person: "",
    site: "",
    project_id: "",
    project_ids: [],
    project_name: "",
  },
  items: [],
  common_make: DEFAULT_COMMON_MAKE,
  notes_html: DEFAULT_PO_NOTES_HTML,
};

const ManagePurchaseOrder = () => {
  const { state: locationData } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get India country code for state/city dropdowns exactly like ManageQuotation
  const indiaCountry = Country.getAllCountries().find(
    (country) => country.name === "India"
  );
  const indiaCode = indiaCountry?.isoCode || "IN";

  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);

  const [poData, setPoData] = useState(initialState);
  const [items, setItems] = useState([{ ...initialItemState }]);
  const [error, setError] = useState({});
  const [itemErrors, setItemErrors] = useState([{}]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [partyData, setPartyData] = useState([]);
  const [projectData, setProjectData] = useState([]);
  const [canAddMoreItems, setCanAddMoreItems] = useState(false);

  const [taxSummary, setTaxSummary] = useState({
    subtotal: 0,
    totalWeight: 0,
    cgst: 0,
    sgst: 0,
    igst: 0,
    roundOff: 0,
    total: 0,
  });

  const firmData = useSelector((state) => state?.getFirm?.user?.data);
  const poResponse = useSelector(
    (state) => state?.getAdminOnePurchaseOrder?.data
  );
  const editData = poResponse?.data || (poResponse?._id ? poResponse : null);
  const { loading: addLoading } = useSelector(
    (state) => state?.addAdminPurchaseOrder || {}
  );
  const { loading: updateLoading } = useSelector(
    (state) => state?.updateAdminPurchaseOrder || {}
  );
  const isLoading = addLoading || updateLoading;

  useEffect(() => {
    if (indiaCode) {
      setStateList(State.getStatesOfCountry(indiaCode));
    }
  }, [indiaCode]);

  useEffect(() => {
    if (poData.vendor_snapshot.state && indiaCode) {
      const selectedStateCode = State.getStatesOfCountry(indiaCode).find(
        (st) =>
          st.name.toLowerCase().trim() ===
          poData.vendor_snapshot.state.toLowerCase().trim()
      )?.isoCode;

      if (selectedStateCode) {
        setCityList(City.getCitiesOfState(indiaCode, selectedStateCode));
      } else {
        setCityList([]);
      }
    } else {
      setCityList([]);
    }
  }, [poData.vendor_snapshot.state, indiaCode]);

  useEffect(() => {
    dispatch(getFirm());
    fetchParties();
    fetchProjects();
  }, [dispatch]);

  const fetchParties = async () => {
    try {
      const headers = { Authorization: "Bearer " + localStorage.getItem("VA_TOKEN") };
      const res = await axios.get(`${V_URL}/admin/get-admin-party`, { headers });
      if (res.data?.success && Array.isArray(res.data?.data)) {
        setPartyData(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching party list:", err);
    }
  };

  const fetchProjects = async () => {
    try {
      const headers = { Authorization: "Bearer " + localStorage.getItem("VA_TOKEN") };
      const res = await axios.get(`${V_URL}/admin/get-admin-project`, { headers });
      if (res.data?.success && Array.isArray(res.data?.data)) {
        setProjectData(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching project list:", err);
    }
  };

  // Auto-set firm exactly like Quotation (firm is disabled, no firm selection by user)
  useEffect(() => {
    if (firmData && firmData.length > 0 && !locationData?._id) {
      const fId = firmData[0]._id;
      setPoData((prev) => ({
        ...prev,
        firm_id: fId,
      }));
    }
  }, [firmData, locationData?._id]);

  useEffect(() => {
    if (locationData?._id) {
      dispatch(getAdminOnePurchaseOrder(locationData._id));
    } else {
      dispatch(resetGetAdminOnePo());
      setPoData(initialState);
      setItems([{ ...initialItemState }]);
      setItemErrors([{}]);
    }
  }, [dispatch, locationData?._id]);

  useEffect(() => {
    return () => {
      dispatch(resetGetAdminOnePo());
    };
  }, [dispatch]);

  useEffect(() => {
    if (editData && locationData?._id) {
      let initialProjectIds = [];
      if (
        Array.isArray(editData.order_details?.project_ids) &&
        editData.order_details.project_ids.length > 0
      ) {
        initialProjectIds = editData.order_details.project_ids
          .map((p) => (p && typeof p === "object" && p._id ? p._id : p))
          .filter(Boolean);
      } else if (editData.order_details?.project_id) {
        const pid =
          editData.order_details.project_id &&
            typeof editData.order_details.project_id === "object" &&
            editData.order_details.project_id._id
            ? editData.order_details.project_id._id
            : editData.order_details.project_id;
        if (pid) initialProjectIds = [pid];
      }

      setPoData({
        firm_id: editData.firm_id?._id || editData.firm_id || "",
        po_no: editData.po_no || "",
        po_date: editData.po_date
          ? moment(editData.po_date).format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD"),
        vendor_id: editData.vendor_id?._id || editData.vendor_id || "",
        vendor_snapshot: {
          name: editData.vendor_snapshot?.name || "",
          address_line1: editData.vendor_snapshot?.address_line1 || "",
          address_line2: editData.vendor_snapshot?.address_line2 || "",
          city: editData.vendor_snapshot?.city || "Surat",
          state: editData.vendor_snapshot?.state || "Gujarat",
          pincode: editData.vendor_snapshot?.pincode || "",
          gst_no: editData.vendor_snapshot?.gst_no || "",
        },
        order_details: {
          contact_person: editData.order_details?.contact_person || "",
          site: editData.order_details?.site || "",
          project_id:
            editData.order_details?.project_id?._id ||
            editData.order_details?.project_id ||
            initialProjectIds[0] ||
            "",
          project_ids: initialProjectIds,
          project_name: editData.order_details?.project_name || "",
        },
        common_make: editData.common_make || DEFAULT_COMMON_MAKE,
        notes_html: editData.notes_html || DEFAULT_PO_NOTES_HTML,
      });

      if (editData.items && editData.items.length > 0) {
        const clonedItems = editData.items.map((it, idx) => ({
          sr_no: it.sr_no || idx + 1,
          item_id: it.item_id?._id || it.item_id || null,
          item_description: it.item_description || "",
          weight: it.weight ?? 0,
          unit: it.unit || "KGS",
          pcs: it.pcs ?? 0,
          rate: it.rate ?? 0,
          amount: it.amount ?? 0,
          make: it.make || editData.common_make || "",
        }));
        setItems(clonedItems);
        setItemErrors(clonedItems.map(() => ({})));
      }
    }
  }, [editData, locationData?._id]);

  // Check if current item row is valid for adding more (Description, Unit, Weight/Pcs, Rate)
  useEffect(() => {
    const currentItem = items[items.length - 1];
    const hasQuantity =
      parseFloat(currentItem?.weight) > 0 || parseFloat(currentItem?.pcs) > 0;
    const isValid =
      currentItem?.item_description?.trim() &&
      currentItem?.unit?.trim() &&
      hasQuantity &&
      currentItem?.rate &&
      parseFloat(currentItem.rate) > 0;
    setCanAddMoreItems(Boolean(isValid));
  }, [items]);

  const calculateTaxType = () => {
    const effectiveFirmId = poData.firm_id?._id || poData.firm_id;
    const selectedFirm = firmData?.find((f) => f._id === effectiveFirmId);
    const firmState = selectedFirm?.state?.toLowerCase()?.trim();
    const vendorState = poData.vendor_snapshot?.state?.toLowerCase()?.trim();

    if (firmState && vendorState && firmState === vendorState) {
      return "cgst_sgst";
    }
    return "igst";
  };

  const taxType = calculateTaxType();

  // Recalculate taxes and totals whenever items or firm/vendor changes
  useEffect(() => {
    let subtotal = 0;
    let totalWeight = 0;

    items.forEach((item) => {
      const amt = parseFloat(item.amount) || 0;
      const wt = parseFloat(item.weight) || 0;
      subtotal += amt;
      totalWeight += wt;
    });

    const taxAmount = (subtotal * 18) / 100;
    let cgst = 0;
    let sgst = 0;
    let igst = 0;

    if (taxType === "cgst_sgst") {
      cgst = taxAmount / 2;
      sgst = taxAmount / 2;
    } else {
      igst = taxAmount;
    }

    const grandTotalRaw = subtotal + taxAmount;
    const finalTotal = Math.round(grandTotalRaw);
    const roundOff = finalTotal - grandTotalRaw;

    setTaxSummary({
      subtotal,
      totalWeight,
      cgst,
      sgst,
      igst,
      roundOff,
      total: finalTotal,
    });
  }, [items, taxType, poData.firm_id, poData.vendor_snapshot?.state]);

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value;

    if (field === "weight" || field === "rate" || field === "pcs") {
      const weight = parseFloat(updatedItems[index].weight) || 0;
      const rate = parseFloat(updatedItems[index].rate) || 0;
      const pcs = parseFloat(updatedItems[index].pcs) || 0;

      if (weight > 0) {
        updatedItems[index].amount = (weight * rate).toFixed(2);
      } else {
        updatedItems[index].amount = (pcs * rate).toFixed(2);
      }
    }

    setItems(updatedItems);

    const updatedItemErrors = [...itemErrors];
    if (updatedItemErrors[index]) {
      if (updatedItemErrors[index][field]) {
        delete updatedItemErrors[index][field];
      }
      if (field === "weight" || field === "pcs") {
        delete updatedItemErrors[index]["weight"];
      }
      setItemErrors(updatedItemErrors);
    }
  };

  const handleAddItem = () => {
    const lastItem = items[items.length - 1];
    const hasQuantity =
      parseFloat(lastItem?.weight) > 0 || parseFloat(lastItem?.pcs) > 0;
    if (
      !lastItem?.item_description?.trim() ||
      !lastItem?.unit?.trim() ||
      !hasQuantity ||
      !lastItem?.rate ||
      parseFloat(lastItem.rate) <= 0
    ) {
      toast.error(
        "Please complete current item details (Description, Unit, Weight or Pcs, Rate) before adding a new item"
      );
      return;
    }

    setItems([
      ...items,
      { ...initialItemState },
    ]);
    setItemErrors([...itemErrors, {}]);
    toast.success("New item row added");
  };

  const handleRemoveItem = (index) => {
    if (items.length === 1) {
      toast.error("At least one item is required");
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this item?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        setItems(items.filter((_, i) => i !== index));
        setItemErrors(itemErrors.filter((_, i) => i !== index));
        toast.success("Item deleted");
      }
    });
  };

  const handleChange = (section, field, value) => {
    if (section === "vendor_snapshot" || section === "order_details") {
      setPoData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }));
    } else {
      setPoData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    if (error[field] || error[`party_${field}`]) {
      setError((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        delete newErrors[`party_${field}`];
        return newErrors;
      });
    }
  };

  const handlePartySelect = (partyId) => {
    const selected = partyData.find((p) => p._id === partyId);
    if (selected) {
      setPoData((prev) => ({
        ...prev,
        vendor_id: selected._id,
        vendor_snapshot: {
          name: selected.name || "",
          address_line1: selected.address || "",
          address_line2: selected.address_two || "",
          city: selected.city || "Surat",
          state: selected.state || "Gujarat",
          pincode: selected.pincode || "",
          gst_no: selected.gstNumber || selected.gst_no || "",
        },
      }));
    }
  };

  const handleProjectSelect = (selectedIds = []) => {
    const selectedObjs = projectData.filter((p) => selectedIds.includes(p._id));
    const projectNames = selectedObjs
      .map((p) => (p.project_no ? `${p.name} - ${p.project_no}` : p.name))
      .join(", ");

    setPoData((prev) => ({
      ...prev,
      order_details: {
        ...prev.order_details,
        project_ids: selectedIds,
        project_id: selectedIds[0] || null,
        project_name: projectNames,
      },
    }));
  };

  const validateForm = () => {
    const errs = {};
    if (!poData.firm_id) errs.firm_id = "Firm is required";
    if (!poData.po_date) errs.po_date = "Order Date is required";
    if (!poData.vendor_snapshot?.name?.trim())
      errs.party_name = "Party name is required";
    if (!poData.vendor_snapshot?.address_line1?.trim())
      errs.party_address = "Address is required";
    if (!poData.vendor_snapshot?.state?.trim())
      errs.party_state = "State is required";

    let hasItemErrors = false;
    const newItemErrors = items.map((item) => {
      const itemError = {};
      if (!item.item_description || !item.item_description.trim()) {
        itemError.item_description = "Required";
        hasItemErrors = true;
      }
      if (!item.unit || !item.unit.trim()) {
        itemError.unit = "Required";
        hasItemErrors = true;
      }
      const wt = parseFloat(item.weight) || 0;
      const pcs = parseFloat(item.pcs) || 0;
      if (wt <= 0 && pcs <= 0) {
        itemError.weight = "Required";
        hasItemErrors = true;
      }
      if (!item.rate || parseFloat(item.rate) <= 0) {
        itemError.rate = "Required";
        hasItemErrors = true;
      }
      return itemError;
    });

    if (hasItemErrors) {
      errs.items = "Please fill all required item fields (Description, Unit, Weight or Pcs, Rate)";
      setItemErrors(newItemErrors);
    }

    setError(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      toast.error("Please fill all required fields");
      return;
    }

    const payload = {
      firm_id: poData.firm_id,
      po_no: poData.po_no ? poData.po_no.trim() : "",
      po_date: poData.po_date,
      vendor_id: poData.vendor_id || null,
      vendor_snapshot: poData.vendor_snapshot,
      order_details: {
        contact_person: poData.order_details.contact_person || "",
        site: poData.order_details.site || "",
        project_id:
          poData.order_details.project_ids?.[0] ||
          poData.order_details.project_id ||
          null,
        project_ids: poData.order_details.project_ids || [],
        project_name: poData.order_details.project_name || "",
      },
      items: items.map((it, idx) => ({
        sr_no: idx + 1,
        item_description: it.item_description,
        weight: parseFloat(it.weight) || 0,
        unit: it.unit || "KGS",
        pcs: parseFloat(it.pcs) || 0,
        rate: parseFloat(it.rate) || 0,
        amount: parseFloat(it.amount) || 0,
        make: poData.common_make || "",
      })),
      common_make: poData.common_make,
      notes_html: poData.notes_html,
      gst_percent: 18,
    };

    if (locationData?._id) {
      dispatch(
        updateAdminPurchaseOrder({ id: locationData?._id, data: payload })
      ).then((response) => {
        if (
          response?.payload?.success ||
          response?.meta?.requestStatus === "fulfilled"
        ) {
          navigate("/admin/purchase-order-management");
        }
      });
    } else {
      dispatch(addAdminPurchaseOrder(payload)).then((response) => {
        if (
          response?.payload?.success ||
          response?.meta?.requestStatus === "fulfilled"
        ) {
          navigate("/admin/purchase-order-management");
        }
      });
    }
  };

  const handleReset = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "All form data will be cleared!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, reset it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        setPoData(initialState);
        setItems([{ ...initialItemState }]);
        setError({});
        setItemErrors([{}]);
        if (firmData && firmData.length > 0) {
          const fId = firmData[0]._id;
          setPoData((prev) => ({
            ...prev,
            firm_id: fId,
          }));
        }
        toast.success("Form has been reset");
      }
    });
  };

  const handleOpen = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <div className={`main-wrapper ${isSidebarOpen ? "slide-nav" : ""}`}>
      <Header handleOpen={handleOpen} />
      <Sidebar />

      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/admin/dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right"></i>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/admin/purchase-order-management">
                      Purchase Order List
                    </Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right"></i>
                  </li>
                  <li className="breadcrumb-item active">
                    {locationData?._id ? "Edit" : "Add"} Purchase Order
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">
                  {/* Header Section */}
                  <div className="row">
                    <div className="col-12">
                      <div className="form-heading mb-4">
                        <h4 className="mb-0">
                          {locationData?._id ? "Edit" : "Add"} Purchase Order Details
                        </h4>
                      </div>
                    </div>
                  </div>

                  {/* PO Details Component (Matching QuotationDetails - Firm disabled, no FY, state & city dropdowns, MultiSelect projects) */}
                  <PoDetails
                    poData={poData}
                    error={error}
                    firmData={firmData}
                    partyData={partyData}
                    projectData={projectData}
                    stateList={stateList}
                    cityList={cityList}
                    handleChange={handleChange}
                    handlePartySelect={handlePartySelect}
                    handleProjectSelect={handleProjectSelect}
                  />

                  {/* Items Component (Matching ItemDetails - Amount disabled/readOnly, no make column, no unit/pcs totals, required unit and strict item validation) */}
                  <PoItems
                    items={items}
                    itemErrors={itemErrors}
                    handleItemChange={handleItemChange}
                    handleAddItem={handleAddItem}
                    handleRemoveItem={handleRemoveItem}
                    canAddMoreItems={canAddMoreItems}
                    totalWeight={taxSummary.totalWeight}
                    subtotal={taxSummary.subtotal}
                    isLoading={isLoading}
                    error={error}
                  />

                  {/* Terms and Tax Summary (Matching TermsAndTaxSummary - No intra-state text) */}
                  <PoTermsAndTax
                    poData={poData}
                    handleChange={handleChange}
                    taxSummary={taxSummary}
                    taxType={taxType}
                  />

                  {/* Submit Buttons (Only Submit/Update and Reset, No Cancel button) */}
                  <div className="row mt-4">
                    <div className="col-12">
                      <div className="doctor-submit text-end">
                        <button
                          type="button"
                          className="btn btn-primary submit-form me-2"
                          onClick={handleSubmit}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Processing...
                            </>
                          ) : (
                            <>{locationData?._id ? "Update" : "Submit"}</>
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary cancel-form"
                          onClick={handleReset}
                          disabled={isLoading}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagePurchaseOrder;
